import { Controller } from 'stimulus'

/**
 * @class TableOfContentsController
 * @extends Controller
 */
export default class extends Controller {
  static targets = [
    'desktopNav',
    'desktopList',
    'mobileDropdown',
    'mobileList',
    'mobileLabel',
    'mobileToggle',
    'mobileSentinel'
  ]

  /**
   * @function TableOfContentsController.connect
   */
  connect() {
    this._onScroll = this._onScroll.bind(this)
    this._onResize = this._onResize.bind(this)
    this._header = document.querySelector('.header__wrapper')

    const copyContainer = document.querySelector('.article__copy-container')
    if (!copyContainer) return

    const headings = Array.from(copyContainer.querySelectorAll('h2'))
    const tldrEl = document.querySelector('.tldr--new-layout')

    if (headings.length === 0 && !tldrEl) {
      if (this.hasDesktopNavTarget) {
        this.desktopNavTarget.setAttribute('aria-hidden', 'true')
        this.desktopNavTarget.classList.add('article-toc--hidden')
      }
      if (this.hasMobileDropdownTarget) this.mobileDropdownTarget.classList.add('article-toc__mobile--hidden')
      return
    }

    this._headingMap = {}
    const usedSlugs = {}

    headings.forEach(heading => {
      const id = this._uniqueSlug(heading.textContent, usedSlugs)
      heading.id = id
      heading.classList.add('article-toc__anchor')
      this._headingMap[id] = heading

      const text = heading.textContent.trim()

      ;[ this.desktopListTarget, this.mobileListTarget ].forEach(list => {
        const li = document.createElement('li')
        const a = document.createElement('a')
        a.href = `#${id}`
        a.textContent = text
        a.classList.add('article-toc__link')
        a.dataset.headingId = id
        li.appendChild(a)
        list.appendChild(li)
      })
    })

    if (tldrEl) {
      this._headingMap['key-takeaways'] = tldrEl
      ;[ this.desktopListTarget, this.mobileListTarget ].forEach(list => {
        const li = document.createElement('li')
        const a = document.createElement('a')
        a.href = '#key-takeaways'
        a.textContent = 'Key Takeaways'
        a.classList.add('article-toc__link')
        a.dataset.headingId = 'key-takeaways'
        li.appendChild(a)
        list.prepend(li)
      })
    }

    [ this.desktopListTarget, this.mobileListTarget ].forEach(list => {
      list.addEventListener('click', e => {
        const a = e.target.closest('a[data-heading-id]')
        if (!a) return
        e.preventDefault()
        a.blur()
        const heading = this._headingMap[a.dataset.headingId]
        if (!heading) return
        // Close the dropdown synchronously before measuring, so that an open
        // in-flow dropdown (non-sticky) does not shift the page content downward
        // and produce a scroll target that overshoots past the intended heading.
        if (this.hasMobileDropdownTarget &&
            this.mobileDropdownTarget.classList.contains('article-toc__mobile--open')) {
          if (this.hasMobileListTarget) this.mobileListTarget.style.transition = 'none'
          this.mobileDropdownTarget.classList.remove('article-toc__mobile--open')
          void this.mobileDropdownTarget.offsetHeight // force synchronous reflow
          if (this.hasMobileListTarget) this.mobileListTarget.style.transition = ''
        }
        if (this.hasMobileToggleTarget) {
          this.mobileToggleTarget.setAttribute('aria-expanded', 'false')
        }

        const headerHeight = this._header ? this._header.getBoundingClientRect().bottom : 0
        const mobileIsSticky = this.hasMobileDropdownTarget &&
          this.mobileDropdownTarget.classList.contains('article-toc__mobile--sticky')
        const buffer = 16
        const headingPageTop = heading.getBoundingClientRect().top + window.scrollY
        const sentinelTop = this.hasMobileSentinelTarget ?
          this.mobileSentinelTarget.getBoundingClientRect().top + window.scrollY :
          0
        const tentativeTop = headingPageTop - headerHeight - buffer
        const toggleHeight = this.hasMobileToggleTarget ? this.mobileToggleTarget.offsetHeight : 0
        const willBeSticky = mobileIsSticky || tentativeTop >= sentinelTop - headerHeight
        const stickyBarHeight = willBeSticky ?
          (mobileIsSticky && this.hasMobileDropdownTarget ?
            this.mobileDropdownTarget.getBoundingClientRect().bottom - headerHeight :
            toggleHeight + (toggleHeight > 0 ? 8 : 0)) : 0
        const targetTop = headingPageTop - headerHeight - stickyBarHeight - buffer
        this._pendingActiveId = a.dataset.headingId
        window.scrollTo({ top: targetTop, behavior: 'smooth' })
        this._updateActiveLink()

        if (this._scrollEndTimeout) clearTimeout(this._scrollEndTimeout)
        if (this._scrollEndListener) window.removeEventListener('scrollend', this._scrollEndListener)
        const clearPending = () => {
          clearTimeout(this._scrollEndTimeout)
          window.removeEventListener('scrollend', this._scrollEndListener)
          if (this._pendingActiveId) {
            const pendingEl = this._headingMap[this._pendingActiveId]
            if (pendingEl) {
              const hdrH = this._header ? this._header.getBoundingClientRect().bottom : 0
              const mobileSticky = this.hasMobileDropdownTarget &&
                this.mobileDropdownTarget.classList.contains('article-toc__mobile--sticky')
              const thresh = (mobileSticky && this.hasMobileDropdownTarget ?
                this.mobileDropdownTarget.getBoundingClientRect().bottom :
                hdrH) + 16
              // If the scroll stopped short and the heading hasn't crossed the
              // threshold yet, leave _pendingActiveId set so the label stays on
              // the intended section. userScrolledBack will clear it on next scroll.
              if (pendingEl.getBoundingClientRect().top > thresh) return
            }
            this._pendingActiveId = null
            this._updateActiveLink()
          }
        }
        this._scrollEndListener = clearPending
        window.addEventListener('scrollend', clearPending, { once: true })
        this._scrollEndTimeout = setTimeout(clearPending, 1000)
      })
    })

    this._setupScrollTracking(headings, tldrEl)

    const headerHeight = this._header ? this._header.getBoundingClientRect().bottom : 0
    document.documentElement.style.setProperty('--toc-scroll-margin', (headerHeight + 16) + 'px')

    window.addEventListener('scroll', this._onScroll)
    window.addEventListener('resize', this._onResize)
  }

  /**
   * @function TableOfContentsController.toggleMobile
   */
  toggleMobile() {
    if (!this.hasMobileDropdownTarget) return
    const isOpen = this.mobileDropdownTarget.classList.toggle('article-toc__mobile--open')
    if (this.hasMobileToggleTarget) {
      this.mobileToggleTarget.setAttribute('aria-expanded', isOpen ? 'true' : 'false')
    }
  }

  /**
   * @function TableOfContentsController.teardown
   */
  teardown() {
    if (this._rafId) cancelAnimationFrame(this._rafId)
    if (this._scrollEndTimeout) clearTimeout(this._scrollEndTimeout)
    if (this._scrollEndListener) window.removeEventListener('scrollend', this._scrollEndListener)
    window.removeEventListener('scroll', this._onScroll)
    window.removeEventListener('resize', this._onResize)
  }

  /**
   * Build the ordered list of tracked elements and set the initial active link.
   * @function TableOfContentsController._setupScrollTracking
   * @param {HTMLElement[]} headings
   * @param {HTMLElement|null} tldrEl
   */
  _setupScrollTracking(headings, tldrEl) {
    // Keep elements in top-to-bottom DOM order so the scroll check
    // can find the last one whose top has crossed the threshold.
    this._trackedElements = []
    if (tldrEl) this._trackedElements.push(tldrEl)
    headings.forEach(h => this._trackedElements.push(h))
    this._updateActiveLink()
  }

  /**
   * Determine which section is currently active and update TOC links.
   * The active section is the last tracked element whose top edge has
   * scrolled at or above the bottom of the sticky header bar.
   * @function TableOfContentsController._updateActiveLink
   */
  _updateActiveLink() {
    const currentScrollY = window.scrollY
    const headerHeight = this._header ? this._header.getBoundingClientRect().bottom : 0
    const mobileIsSticky = this.hasMobileDropdownTarget &&
      this.mobileDropdownTarget.classList.contains('article-toc__mobile--sticky')
    // Add the same 16px buffer used by click navigation so that the TOC activates
    // and deactivates headings at the exact screen position where clicking a link
    // would land them — eliminating the visible mismatch when scrolling up.
    const buffer = 16
    const threshold = (mobileIsSticky && this.hasMobileDropdownTarget ?
      this.mobileDropdownTarget.getBoundingClientRect().bottom :
      headerHeight) + buffer

    let activeEl = null
    for (const el of this._trackedElements) {
      if (el.getBoundingClientRect().top <= threshold) {
        activeEl = el
      }
    }

    let id = activeEl ? activeEl.id : null

    if (this._pendingActiveId) {
      const pendingEl = this._headingMap[this._pendingActiveId]
      // If scrollY decreased since the last frame, the user is scrolling up — abandon pending
      const userScrolledBack = this._prevScrollY !== undefined && currentScrollY < this._prevScrollY
      if (!pendingEl || userScrolledBack) {
        this._pendingActiveId = null
      } else if (pendingEl.getBoundingClientRect().top <= threshold) {
        this._pendingActiveId = null
      } else {
        id = this._pendingActiveId
      }
    }

    this._prevScrollY = currentScrollY

    if (id === this._lastActiveId) return
    this._lastActiveId = id

    ;[ this.desktopListTarget, this.mobileListTarget ].forEach((list, i) => {
      const isMobileList = i === 1
      list.querySelectorAll('.article-toc__link').forEach(a => a.classList.remove('article-toc__link--active'))
      if (!id) return
      const activeLink = list.querySelector(`a[href="#${id}"]`)
      if (activeLink) {
        activeLink.classList.add('article-toc__link--active')
        if (isMobileList && this.hasMobileLabelTarget) {
          this.mobileLabelTarget.textContent = activeLink.textContent.trim()
        }
      }
    })

    if (id && this.hasDesktopListTarget) {
      const activeDesktopLink = this.desktopListTarget.querySelector(`a[href="#${id}"]`)
      if (activeDesktopLink) activeDesktopLink.scrollIntoView({ block: 'nearest' })
    }
  }

  /**
   * @function TableOfContentsController._onScroll
   */
  _onScroll() {
    if (this._rafId) return
    this._rafId = requestAnimationFrame(() => {
      this._rafId = null
      this._updateStickyState()
      this._updateActiveLink()
    })
  }

  /**
   * Toggle the mobile sticky bar on/off and update its position.
   * Uses a small hysteresis offset so the sticky state does not oscillate
   * when the sentinel is right at the header threshold.
   * @function TableOfContentsController._updateStickyState
   */
  _updateStickyState() {
    if (!this.hasMobileDropdownTarget) return

    const headerHeight = this._header ? this._header.getBoundingClientRect().bottom : 0
    const sentinelTop = this.hasMobileSentinelTarget ?
      this.mobileSentinelTarget.getBoundingClientRect().top :
      Infinity
    const isDropdownVisible = window.getComputedStyle(this.mobileDropdownTarget).display !== 'none'
    const isSticky = this.mobileDropdownTarget.classList.contains('article-toc__mobile--sticky')

    // Hysteresis: become sticky when sentinel hits the header, but only
    // un-sticky once it is 4px below the header. This prevents the layout
    // from oscillating (and shaking) right at the transition point.
    const shouldBecomeSticky = isDropdownVisible && !isSticky && sentinelTop <= headerHeight
    const shouldUnsticky     = isSticky && (!isDropdownVisible || sentinelTop > headerHeight + 4)

    if (shouldBecomeSticky) {
      // Measure the full vertical space the TOC occupies in the flow BEFORE it
      // goes position:fixed. Assigning that height to the sentinel keeps the
      // layout intact — without it, the space collapses and the key-takeaways
      // heading jumps up and is immediately covered by the newly-fixed bar.
      const style = window.getComputedStyle(this.mobileDropdownTarget)
      const totalSpace = this.mobileDropdownTarget.offsetHeight +
        parseFloat(style.marginTop) +
        parseFloat(style.marginBottom)
      this.mobileDropdownTarget.classList.add('article-toc__mobile--sticky')
      if (this.hasMobileSentinelTarget) {
        this.mobileSentinelTarget.style.height = totalSpace + 'px'
      }
      this._setStickyPosition()
    } else if (shouldUnsticky) {
      this.mobileDropdownTarget.classList.remove('article-toc__mobile--sticky')
      Object.assign(this.mobileDropdownTarget.style, { boxSizing: '', left: '', paddingLeft: '', paddingRight: '', right: '', top: '', width: '' })
      if (this.hasMobileSentinelTarget) {
        this.mobileSentinelTarget.style.height = '0'
      }
      document.documentElement.style.setProperty('--toc-scroll-margin', '1rem')
    }

    const article = document.querySelector('.article--new-layout')
    if (article) {
      const articleBottom = article.getBoundingClientRect().bottom
      this.mobileDropdownTarget.classList.toggle(
        'article-toc__mobile--past-article',
        articleBottom < 0
      )
    }
  }

  /**
   * @function TableOfContentsController._setStickyPosition
   */
  _setStickyPosition() {
    const primary = document.querySelector('.article-new-layout__primary')
    if (!primary) return
    const headerHeight = this._header ? this._header.getBoundingClientRect().bottom : 0
    const rect = primary.getBoundingClientRect()
    const padLeft = rect.left
    const padRight = window.innerWidth - rect.right

    Object.assign(this.mobileDropdownTarget.style, {
      boxSizing:    'border-box',
      left:         '0',
      paddingLeft:  padLeft + 'px',
      paddingRight: padRight + 'px',
      right:        '0',
      top:          headerHeight + 'px',
      width:        '100%'
    })

    const tocBottom = this.mobileDropdownTarget.getBoundingClientRect().bottom
    document.documentElement.style.setProperty('--toc-scroll-margin', tocBottom + 'px')
  }

  /**
   * @function TableOfContentsController._onResize
   */
  _onResize() {
    if (this.hasMobileDropdownTarget &&
        this.mobileDropdownTarget.classList.contains('article-toc__mobile--sticky')) {
      this._setStickyPosition()
    } else {
      const headerHeight = this._header ? this._header.getBoundingClientRect().bottom : 0
      document.documentElement.style.setProperty('--toc-scroll-margin', (headerHeight + 16) + 'px')
    }
  }

  /**
   * @function TableOfContentsController._uniqueSlug
   * @param {string} text
   * @param {Object} usedSlugs
   * @return {string}
   */
  _uniqueSlug(text, usedSlugs) {
    const base = text.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '')
    if (!usedSlugs[base]) {
      usedSlugs[base] = 1
      return base
    }
    usedSlugs[base] += 1
    return `${base}-${usedSlugs[base]}`
  }
}
